import React, { useMemo, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { useCollect, useRequest } from 'hooks';

import { TASK_TYPES } from 'utils/Constants';
import Validator from 'utils/Validator';

import Show from 'components/Show';
import { Title } from 'components/Misc/Typography';
import { Button, Form, Input, Type } from 'components/Misc/Form';
import Editor from 'components/Editor';

import Task from 'containers/Shared/Tasks/Task';

import { useExamProfileStore } from 'store/exam/useExamProfileStore';
import { useTaskCreateStore } from 'store/task/useTaskCreateStore';

const Edit = ({ setTab }) => {
	// Store exam's id
	const { taskId } = useParams();

	// Use router's history
	const history = useHistory();

	// Store update request response
	const [[progress], request] = useRequest('put', `tasks/${taskId}`);

	// Store remove request response
	const [[removeProgress], remove] = useRequest('delete', `tasks/${taskId}`);
	
	// Update selected tab
	useEffect(() => setTab(null), [setTab]);

	// Use exam's profile store
	const slug = useExamProfileStore(state => state.exam.slug);
	
	// Use task's create store
	const [task, onChange, onAddQuestion, onInit, onReset] = useTaskCreateStore(state => [
		state.task,
		state.onChange,
		state.onAddQuestion,
		state.onInit,
		state.onReset
	]);

	// Create request parameters
	const parameters = useMemo(() => ({
		eager: 'questions.[options, words]'
	}), []);

	// Create request with parameters
	const [[result], collect] = useCollect(`tasks/${taskId}`, parameters);

	useEffect(() => {
		// Collect task if id recieved
		if (taskId) (async () => await collect())();
	}, [collect]);

	useEffect(() => {
		// Initialize task's data after collection
		if (result) onInit(result);
	}, [result]);

	useEffect(() => {
		// Reset exam's data on unmount
		return () => onReset();
	}, []);

	// Handles form's onSubmit event
	// @event: object | onSubmit event
	const onSubmit = async (event) => {
		event.preventDefault();

		// Do not submit if request in progress
		if (progress) return;

		// Validate task's data
		Validator.validate('/task', task);

		// Try to update task
		const [success, error] = await request(task);

		// TODO: Think of a better way of handling success
		if (success && success.id !== Number(taskId)) {
			return history.push(`/exams/tasks/edit/${success.examId}/${success.id}`);
		}

		// TODO: Thin of a better way of handling errors
		if (error) return console.log(error.message);
	};

	// Handles form's onSubmit event
	// @event: object | onSubmit event
	const onDelete = async (event) => {
		event.preventDefault();
		
		// Try to update task
		const [success, error] = await remove();

		// TODO: Think of a better way of handling success
		if (success) {
			return history.push(`/exams/${success.examId}`);
		}

		// TODO: Thin of a better way of handling errors
		if (error) return console.log(error.message);
	};

	// TODO: Remove after testing
	useEffect(() => console.log('TASK.EDIT:', taskId, task), [taskId, task]);

	return (
		<>
			<Form
				onSubmit={onSubmit}
				className="mt-4 p-4 grid grid-cols-2 gap-4 bg-white dark:bg-neutral-800 rounded-lg transition-colors"
			>
				<Title
					title="Pamatinformācija"
					className="col-span-2 text-xl"
				/>

				<Input
					type="number"
					name="points"
					label="Punkti par pareizu atbildi"
					value={task.points}
					onChange={onChange}
				/>

				<Input
					type="number"
					name="sample"
					label="Jautājumu skaits"
					value={task.sample}
					onChange={onChange}
				/>

				<Title
					title="Tips"
					className="col-span-2 text-xl"
				/>

				<Type
					readOnly
					name="type"
					value={task.type}
					onChange={onChange}
					options={TASK_TYPES}
					className="col-span-2"
				/>

				<Title
					title="Uzdevuma apraksts"
					className="col-span-2 text-xl"
				/>

				<div className="col-span-2">
					<Editor
						type="description"
						name="description"
						path="exams"
						folder={slug}
						value={task.description}
						onChange={onChange}
					/>
				</div>

				{/* Task's questions */}
				<Show if={task.questions?.length}>
					<Title
						title="Jautājumi"
						className="col-span-2 text-xl"
					/>

					<div className="col-span-2">
						<Task />
					</div>
				</Show>

				
				<div className="flex justify-end col-span-2">
					<Button
						icon="add"
						value="Pievienot jautājumu"
						className="mr-2"
						onClick={onAddQuestion}
					/>
					
					<Button
						submit
						icon="done"
						value="Saglabāt"
						theme="success"
						progress={progress}
					/>
				</div>
			</Form>

			<Form
				onSubmit={onDelete}
				className="mt-4 p-4 grid grid-cols-2 gap-4 bg-white dark:bg-neutral-800 rounded-lg transition-colors"
			>
				<div className="flex justify-end col-span-2">
					<Button
						submit
						icon="delete"
						value="Dzēst"
						theme="danger"
						progress={removeProgress}
					/>
				</div>
			</Form>
		</>
	);
};

export default Edit;
